import React from 'react';
import PropTypes from 'prop-types';
import { Button } from 'antd';
import './sandbox.css';
import './GettingStarted.css';

/**
 * The getting-started checklist on Home (sandbox-first, docs/SANDBOX_FIRST.md): the first things worth doing, each
 * ticked from what really happened — an AI tool connected (the overview's aiTools), a first project, a preview seen,
 * Flow's chat tried, a project shared (the person's settings `home.*`). It can be hidden for good (Settings brings it
 * back).
 */
export function checklistSteps({ sandbox, settings, projectCount, chatAvailable = true }) {
  const home = settings?.home || {};
  return [
    { key: 'tool', title: 'Connect your AI tool', detail: 'Claude, Gemini CLI, Cursor, Kiro… work on your sandbox.', done: (sandbox?.aiTools || []).length > 0, action: 'Connect' },
    { key: 'project', title: 'Create your first project', detail: 'Here, or by asking your AI tool.', done: projectCount > 0, action: 'Create' },
    { key: 'preview', title: 'See it running', detail: 'Open a project and start its preview.', done: Boolean(home.previewOpened) || (sandbox?.services || []).length > 0, action: 'Open' },
    // Not in the person's plan: not a step.
    ...(chatAvailable ? [{ key: 'chat', title: 'Try Flow’s chat', detail: 'Ask Flow’s assistant to build or fix something.', done: Boolean(home.chatTried), action: 'Try' }] : []),
    { key: 'share', title: 'Share a project', detail: 'Invite a teammate or send a preview link.', done: Boolean(home.shared), action: 'Share' },
  ];
}

export default function GettingStarted({ sandbox = null, settings = null, projectCount = 0, onAction, onDismiss, chatAvailable = true }) {
  const steps = checklistSteps({ sandbox, settings, projectCount, chatAvailable });
  const done = steps.filter((step) => step.done).length;
  const allDone = done === steps.length;
  return (
    <section aria-labelledby="gs-title" className="sbx-card gs">
      <div className="gs-head">
        <div className="gs-title">
          <h2 id="gs-title">{allDone ? 'You’re all set' : 'Get started'}</h2>
          <span>{done} of {steps.length} done</span>
        </div>
        <Button onClick={onDismiss} size="small" type="text">{allDone ? 'Hide' : 'Hide this list'}</Button>
      </div>
      <span aria-label={`${done} of ${steps.length} steps done`} className="sbx-bar" role="img">
        <span className="sbx-bar-fill" style={{ width: `${Math.round((done / steps.length) * 100)}%` }} />
      </span>
      <ol className="gs-steps">
        {steps.map((step) => (
          <li className={`gs-step${step.done ? ' done' : ''}`} key={step.key}>
            <span aria-hidden="true" className="gs-check">{step.done ? '✓' : ''}</span>
            <span className="gs-text">
              <strong>{step.title}</strong>
              <span>{step.detail}</span>
            </span>
            {step.done
              ? <span className="gs-done-label">Done</span>
              : <Button onClick={() => onAction(step.key)} size="small">{step.action}</Button>}
          </li>
        ))}
      </ol>
    </section>
  );
}

GettingStarted.propTypes = {
  sandbox: PropTypes.object,
  settings: PropTypes.object,
  projectCount: PropTypes.number,
  onAction: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
};
